import { ToggleGroup } from 'radix-ui';
import { cn } from '../../lib/cn.js';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Optional tooltip-style hint exposed as the item's title. */
  hint?: string;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  /** Accessible name for the group (e.g. "Medir tiempo de respuesta en"). */
  label: string;
  size?: 'sm' | 'md';
  className?: string;
}

/** Single-choice toggle for switching a view mode (Radix ToggleGroup; arrow keys move between options). */
export function SegmentedControl<T extends string>({ value, onValueChange, options, label, size = 'md', className }: SegmentedControlProps<T>) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      // Radix emits '' when the active item is pressed again; a segmented control always keeps a value.
      onValueChange={(v) => v && onValueChange(v as T)}
      aria-label={label}
      className={cn('inline-flex items-center gap-1 rounded-control border border-border-control bg-surface-subtle p-1', className)}
    >
      {options.map((o) => (
        <ToggleGroup.Item
          key={o.value}
          value={o.value}
          title={o.hint}
          className={cn(
            'rounded-[7px] font-medium text-text-secondary transition-colors duration-[var(--cm-motion-fast)]',
            'hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
            'data-[state=on]:bg-surface-default data-[state=on]:text-text-primary data-[state=on]:shadow-card',
            size === 'sm' ? 'h-7 px-2.5 text-helper' : 'h-8 px-3 text-body',
          )}
        >
          {o.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
